// Ledger kernel — the CANONICAL pure module for WaageWu's expense ledger.
// Types, op reducer, split math, and balance computation live HERE and
// only here. The Worker (src/worker.ts), the web SPA (via the @core
// alias), and mobile (via scripts/sync_ledger_core.sh byte-copy to
// mobile/lib/ledger-core.ts) all consume this one file, so a rounding
// tweak or a new split type lands once.
//
// HARD RULE: zero imports. This file must stay import-free and
// runtime-generic (Workers, Vite, Metro/Hermes) so the byte-copy to
// mobile never drags Worker code across the boundary. Mirrors the
// japan-trip trip-state-core.ts dual-port discipline.
//
// Why ops instead of state snapshots: if clients queued snapshots
// ("set state to X") and the other spouse edited the ledger while a
// phone was offline, replaying the snapshot would clobber their
// changes. Ops describe intent — "add this expense", "tombstone that
// settlement" — so queued intents rebase onto whatever state the
// server holds when the queue drains. The Worker's op endpoints run
// this same reducer server-side (read → applyOp → conditional write).

// ─── Money + identity primitives ─────────────────────────────────────

// READ THIS BEFORE TOUCHING ANY AMOUNT FIELD.
//
// `amountCents` is a legacy NAME, not a definition. Every *Cents field
// on a record holds INTEGER MINOR UNITS OF THAT RECORD'S `currency` —
// never a float, never a major unit:
//
//   $152.34 USD  → amountCents 15234   (USD exponent 2)
//   ¥48,000 JPY  → amountCents 48000   (JPY exponent 0 — NOT 4800000)
//   ₩12,000 KRW  → amountCents 12000   (KRW exponent 0)
//
// The name was kept deliberately: renaming it would churn every
// deployed client, the MCP tool schemas, the KV blobs, and the offline
// op queues at once. The rigorous definition lives here and is repeated
// on every surface that documents the field.
//
// TWO amounts ride on every record:
//
//   amountCents      the NATIVE amount, in minor units of `currency`.
//                    This is what reconciliation compares against a
//                    hotel bill or a card statement.
//   baseAmountCents  the same expense expressed in the household's BASE
//                    currency (meta.currency, "USD"), ALWAYS in USD
//                    cents (exponent 2) regardless of the native code.
//
// BALANCES ARE COMPUTED FROM baseAmountCents ONLY. Summing native
// amounts across currencies would be arithmetic nonsense; the base
// amount is what makes "who owes whom" one number. The server never
// invents a base amount by calling an FX API — the writer supplies it
// (GET /api/fx is a client-side convenience for pre-filling the field,
// never a write-path dependency). The single exception is the
// tautological one: when a record's currency IS the base currency,
// baseAmountCents must equal amountCents, so it can be filled in (and
// migrated) with no exchange rate involved at all.

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Currency table (ISO 4217 minor-unit exponents) ──────────────────
//
// exponent 2 → 1 major unit = 100 minor units ($1.00 = 100 cents).
// exponent 0 → the currency HAS no minor unit: ¥1 = 1 minor unit. A
// zero-decimal currency multiplied by 100 "to be safe" inflates the
// ledger 100×, which is why every documented surface spells this out.
//
// Codes not in this table are REJECTED by the validators rather than
// guessed at — an unknown exponent would silently mis-scale money.
export const CURRENCY_EXPONENTS: Record<string, number> = {
  USD: 2,
  EUR: 2,
  GBP: 2,
  CAD: 2,
  AUD: 2,
  CHF: 2,
  CNY: 2,
  MXN: 2,
  THB: 2,
  SGD: 2,
  TWD: 2,
  JPY: 0,
  KRW: 0,
};

// The household BASE currency. meta.currency is the per-household
// setting, but every household is USD today and baseAmountCents is
// defined as USD cents, so this is the default everywhere a base
// currency is needed and none was passed.
export const BASE_CURRENCY = 'USD';

export const SUPPORTED_CURRENCIES: string[] = Object.keys(CURRENCY_EXPONENTS).sort();

// en-US rendering symbols. Deliberately NOT Intl.NumberFormat: Hermes'
// Intl currency data is incomplete and we want byte-identical output
// across Worker, web, and mobile (this module is byte-copied to
// mobile). Ambiguous "$" currencies carry their region prefix so a MXN
// row can never read as USD.
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'CA$',
  AUD: 'A$',
  CHF: 'CHF ',
  CNY: 'CN¥',
  MXN: 'MX$',
  THB: '฿',
  SGD: 'S$',
  TWD: 'NT$',
  JPY: '¥',
  KRW: '₩',
};

// Uppercase + trim a caller-supplied code, returning null when it is
// not a supported currency. The normalized form is what gets STORED —
// "jpy" and "JPY" must never fork into two currencies in one ledger.
export function normalizeCurrencyCode(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const code = v.trim().toUpperCase();
  return code in CURRENCY_EXPONENTS ? code : null;
}

export function isSupportedCurrency(v: unknown): boolean {
  return normalizeCurrencyCode(v) !== null;
}

// Minor units per major unit, as an ISO exponent. Unsupported codes
// fall back to 2 so a formatter never throws on legacy data; the
// VALIDATORS are what keep unsupported codes out of the ledger.
export function currencyExponent(v: unknown): number {
  const code = normalizeCurrencyCode(v);
  return code === null ? 2 : CURRENCY_EXPONENTS[code];
}

// Render integer minor units as an en-US money string with the correct
// number of decimals for the currency and thousands grouping:
//
//   formatMoney(15234, 'USD') → "$152.34"
//   formatMoney(48000, 'JPY') → "¥48,000"
//   formatMoney(-500,  'USD') → "-$5.00"
//   formatMoney(1234,  'XXX') → "XXX 12.34"   (unknown code → prefix)
//
// Integer math only — no float division, so a large amount can't drift.
export function formatMoney(minor: number, code: string): string {
  const normalized = typeof code === 'string' ? code.trim().toUpperCase() : '';
  const exponent = currencyExponent(normalized);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minor));
  const scale = exponent === 0 ? 1 : 10 ** exponent;
  const whole = Math.floor(abs / scale);
  const frac = exponent === 0 ? '' : '.' + String(abs % scale).padStart(exponent, '0');
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const symbol =
    CURRENCY_SYMBOLS[normalized] ?? (normalized.length > 0 ? `${normalized} ` : '');
  return `${sign}${symbol}${grouped}${frac}`;
}

export type Role = 'owner' | 'viewer';

export interface HouseholdMember {
  email: string;
  role: Role;
  joinedAt: string;
  invitedBy?: string;
}

export interface HouseholdMeta {
  id: string;
  name: string;
  // The household's BASE currency — the one every balance is expressed
  // in, and the currency baseAmountCents counts (USD cents). Records
  // may be written in any supported currency; this is what they are
  // reduced to. "USD" for v1 (no UI changes it).
  currency: string;
  accent?: string; // one of the 6 editorial category names
  members: HouseholdMember[];
  createdAt: string;
  createdBy: string;
  deleted?: boolean;
  deletedAt?: string;
  _meta?: { lastEditedBy: string; lastEditedAt: string };
}

// ─── Split tagged union ──────────────────────────────────────────────
//
//   equal  — every household member owes an even share (default)
//   exact  — explicit per-email minor units; must sum to amountCents
//   shares — proportional positive-integer weights (covers ⅓-share rows)
//   full   — one person owes the whole amount ("they owe all of it")
//
// UNITS OF `exact` (locked decision): sharesCents is expressed in the
// expense's NATIVE currency and must sum to amountCents — that is what
// a person typing "you take ¥30,000 of the ¥48,000 ryokan" sees and
// checks. expenseShares() converts those native shares to BASE units
// pro-rata (see its comment) so balances stay exact.

export type Split =
  | { type: 'equal' }
  | { type: 'exact'; sharesCents: Record<string, number> }
  | { type: 'shares'; shares: Record<string, number> }
  | { type: 'full'; owedBy: string };

// ─── Import source (the cross-app link) ──────────────────────────────
//
// `source` ties a ledger row back to the record it was imported from in
// another app — today Tripper Keeper trip bookings. It is the
// IDEMPOTENCY KEY for imports: within a household at most one
// non-deleted expense may carry a given sourceKey, so pushing the same
// booking twice — from either side, in either order — can never
// double-book the ledger.
//
// All three fields are REQUIRED when source is present. A partial key
// (app-only, or app+trip) would collide across trips/bookings and make
// the uniqueness rule meaningless, so the validator rejects it rather
// than guessing.
export interface ExpenseSource {
  app: string; // origin app slug, 1-40 chars of [a-z0-9-]
  tripId: string; // 1-64 chars, no ':' (TK trip slug)
  bookingId: string; // 1-64 chars, no ':' (TK booking id)
}

export interface Expense {
  id: string; // "exp_{ts36}-{uuid8}"
  date: string; // YYYY-MM-DD
  dateEnd?: string;
  vendor: string;
  note?: string;
  // NATIVE amount: integer MINOR UNITS of `currency`, > 0.
  // ¥48,000 is 48000 (JPY exponent 0); $152.34 is 15234.
  amountCents: number;
  // Supported ISO 4217 code, stored UPPERCASE (see CURRENCY_EXPONENTS).
  currency: string;
  // The same expense in the household BASE currency — ALWAYS USD cents
  // (exponent 2), whatever `currency` is. Balances read this and only
  // this. When currency === base currency it MUST equal amountCents.
  //
  // OPTIONAL IN THE TYPE, REQUIRED AT RUNTIME: every write path rejects
  // a missing/invalid value (validateExpenseRecord → invalid_base_amount).
  // It is `?` only because (a) blobs written before multi-currency have
  // no such field and are migrated on read, and (b) the web/mobile
  // clients — which construct Expense literals against this byte-copied
  // kernel — land in a later pass. Tighten to `number` once they do.
  baseAmountCents?: number;
  paidBy: string; // member email
  split: Split;
  // Reserved fields — accepted + stored, no v1 behavior. This is how
  // categories / CC reconciliation land later without a migration (see
  // plan §Data model). `source` graduated out of "reserved": it is
  // validated (validateSource) and enforced unique per household by the
  // add path in ledger-ops.
  category?: string | null;
  payerSource?: string | null; // e.g. "Chase 1170"
  source?: ExpenseSource | null;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  // Soft-delete tombstone — deleted records stay in the blob (audit +
  // sync safety) and are excluded from balances + default listings.
  deleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface Settlement {
  id: string; // "set_{ts36}-{uuid8}"
  date: string; // YYYY-MM-DD
  from: string; // payer of the settlement (the one clearing their debt)
  to: string; // recipient
  // Same units contract as Expense: native minor units of `currency`…
  amountCents: number;
  currency: string;
  // …plus the USD-cents base amount the balance actually moves by.
  // Runtime-required, TS-optional for the same reasons as on Expense.
  baseAmountCents?: number;
  method?: string; // "venmo" etc.
  note?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  deleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

// One entry per recently committed op. recordId lets a deduped replay
// look up and re-return the record the ORIGINAL commit touched — even
// when the replaying client omitted the record id and the retry minted
// a fresh one (review M1.1 finding: add-replay returned record:null).
// null recordId only occurs on legacy entries migrated from the v1
// string-array shape (see normalizeRecentOps).
export interface RecentOp {
  opId: string;
  recordId: string | null;
}

export interface LedgerState {
  _meta: {
    schemaVersion: 1;
    lastEditedBy?: string;
    lastEditedAt?: string;
    // Reserved null — future compaction hook (settle-up checkpoints let
    // old history collapse into carried-forward pairwise balances with
    // no schema break).
    openingBalances: null;
    // Server-side idempotency window for op replay (mobile queue drains
    // POST ops one-by-one; a retry after a lost response must not
    // double-apply). Capped at RECENT_OPS_CAP, oldest dropped first.
    recentOps: RecentOp[];
  };
  expenses: Expense[];
  settlements: Settlement[];
}

export const RECENT_OPS_CAP = 200;

export function emptyLedgerState(): LedgerState {
  return {
    _meta: { schemaVersion: 1, openingBalances: null, recentOps: [] },
    expenses: [],
    settlements: [],
  };
}

// Migrate/repair a recent-ops list read from storage. Accepts the
// legacy v1 shape (plain opId strings → { opId, recordId: null }), the
// current object shape, or junk (dropped). Always returns a fresh
// array capped at RECENT_OPS_CAP (oldest dropped first).
export function normalizeRecentOps(raw: unknown): RecentOp[] {
  if (!Array.isArray(raw)) return [];
  const out: RecentOp[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string' && entry.length > 0) {
      out.push({ opId: entry, recordId: null });
    } else if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as RecentOp).opId === 'string' &&
      (entry as RecentOp).opId.length > 0
    ) {
      const rid = (entry as RecentOp).recordId;
      out.push({
        opId: (entry as RecentOp).opId,
        recordId: typeof rid === 'string' ? rid : null,
      });
    }
  }
  return out.slice(-RECENT_OPS_CAP);
}

// ─── Base amounts (the balance currency) ─────────────────────────────

// The USD-cents amount a record contributes to balances.
//
// LEGACY FALLBACK: records written before multi-currency have no
// baseAmountCents and are all USD, where base === native by definition
// — so an absent/invalid field reads as amountCents. That single rule
// is the entire migration: it makes every consumer (Worker balance,
// web preview, an offline mobile cache holding a pre-migration blob)
// agree without a rewrite pass, and it is idempotent because a record
// that already has a valid base amount is returned untouched.
export function baseAmountOf(record: {
  amountCents: number;
  baseAmountCents?: number;
}): number {
  const base = record.baseAmountCents;
  return typeof base === 'number' && Number.isInteger(base) && base > 0
    ? base
    : record.amountCents;
}

// Decide the baseAmountCents to STORE for a record being written.
//
// A supplied value always wins (even a bad one — the validator, not
// this helper, judges it, so the caller gets a real error message
// instead of a silently substituted number). When nothing was supplied
// the ONLY fill-in is the tautology: a record denominated in the base
// currency has base === native. Anything else (a JPY expense with no
// base amount) returns undefined and fails validation — the server
// never invents an exchange rate.
export function resolveBaseAmountCents(
  supplied: unknown,
  amountCents: unknown,
  currency: unknown,
  baseCurrency: string = BASE_CURRENCY,
): number | undefined {
  if (supplied !== undefined && supplied !== null) return supplied as number;
  const code = normalizeCurrencyCode(currency);
  const base = normalizeCurrencyCode(baseCurrency) ?? BASE_CURRENCY;
  if (code !== null && code === base && typeof amountCents === 'number') {
    return amountCents;
  }
  return undefined;
}

// The PATCH counterpart of resolveBaseAmountCents.
//
// A patch that changes the amount of a base-currency record without
// naming a new base amount would merge into a record violating
// base === native — a 400 on a change the caller had no reason to think
// was ambiguous (PATCH {amountCents: 4200} on a USD row). So the base
// amount follows the native one, but ONLY in that tautological case:
// re-pricing a foreign-currency record, or switching its currency,
// still demands an explicit baseAmountCents.
//
// Pure: returns the SAME patch reference when there is nothing to add.
// Lives here rather than in the Worker so the offline op queue derives
// the same value for its optimistic local state (otherwise a queued
// edit would show a stale balance until the drain round-trips).
export function withPatchBaseAmount<
  T extends { amountCents?: number; currency?: string; baseAmountCents?: number },
>(patch: T, currentCurrency: unknown, baseCurrency: string = BASE_CURRENCY): T {
  if (patch.amountCents === undefined || patch.baseAmountCents !== undefined) return patch;
  const effective = normalizeCurrencyCode(patch.currency ?? currentCurrency);
  const base = normalizeCurrencyCode(baseCurrency) ?? BASE_CURRENCY;
  if (effective === null || effective !== base) return patch;
  return { ...patch, baseAmountCents: patch.amountCents };
}

// Migrate-on-read for a whole blob: stamp baseAmountCents onto legacy
// records that predate multi-currency (all USD, so base === native).
//
// Returns the SAME state reference when nothing needed migrating, so
// readLedgerState can't be tricked into a pointless KV rewrite, and a
// second pass over migrated state is a no-op.
export function migrateBaseAmounts(state: LedgerState): LedgerState {
  let touched = false;
  const fix = <T extends { amountCents: number; baseAmountCents?: number }>(row: T): T => {
    if (!row || typeof row !== 'object') return row;
    const base = row.baseAmountCents;
    if (typeof base === 'number' && Number.isInteger(base) && base > 0) return row;
    if (typeof row.amountCents !== 'number') return row; // junk row — validators own it
    touched = true;
    return { ...row, baseAmountCents: row.amountCents };
  };
  const expenses = (state.expenses ?? []).map(fix);
  const settlements = (state.settlements ?? []).map(fix);
  return touched ? { ...state, expenses, settlements } : state;
}

// ─── Stable id generation ────────────────────────────────────────────

// "exp_{ts36}-{uuid8}": base36 millisecond timestamp + first 8 hex chars
// of a UUID. Generated at enqueue-time on clients so an optimistic
// insert and the eventual server row share an id (same rationale as
// trip-state-core.genId). Math.random fallback keeps the module
// runnable on runtimes without crypto.randomUUID (older Hermes).
export function genId(prefix: string): string {
  const ts = Date.now().toString(36);
  const rand =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(16).slice(2, 10).padEnd(8, '0');
  return `${prefix}_${ts}-${rand}`;
}

// ─── Source keys (cross-app import identity) ─────────────────────────

// "{app}:{tripId}:{bookingId}" — the household-scoped uniqueness key for
// an imported expense. Both apps derive it the same way from the same
// three fields, so Tripper Keeper can ask "is booking X already in the
// ledger?" and WaageWu can answer without a shared database.
//
// Returns null for an absent/null source (a normal manual expense) and
// for a malformed one (a legacy blob written before validateSource
// existed). CALLERS MUST NEVER TREAT TWO NULLS AS EQUAL — null means
// "not an imported row", not "same import".
export function sourceKey(source: ExpenseSource | null | undefined): string | null {
  if (!source || typeof source !== 'object') return null;
  const { app, tripId, bookingId } = source;
  if (
    typeof app !== 'string' ||
    typeof tripId !== 'string' ||
    typeof bookingId !== 'string' ||
    app.length === 0 ||
    tripId.length === 0 ||
    bookingId.length === 0
  ) {
    return null;
  }
  return `${app}:${tripId}:${bookingId}`;
}

// The LIVE (non-tombstoned) expense carrying `key`, or undefined. This
// is the whole duplicate-detection rule, in one pure place so the
// server guard, the tests, and (via the byte-copy) mobile all agree.
//
// Tombstones are deliberately IGNORED: soft-deleting an imported
// expense is an explicit "no, don't track this booking here", so a
// later re-import must be free to create a fresh row. The tombstone
// stays visible through the sources listing (deleted: true), which is
// how a caller tells "never imported" from "imported then removed".
export function findExpenseBySourceKey(
  expenses: Expense[],
  key: string,
): Expense | undefined {
  return expenses.find((e) => e.deleted !== true && sourceKey(e.source) === key);
}

// ─── Generic list helpers (ported from trip-state-core.ts) ───────────
//
// Every helper operates on the bare array and returns the SAME
// reference when nothing changed, so callers can `next === input` to
// skip a no-op write.

// Spread `patch`'s DEFINED (!== undefined) keys over a shallow clone of
// `base`. Field-agnostic — a new expense field flows through without
// editing this function. (Pass `null` explicitly to clear a field.)
export function mergeDefined<T>(base: T, patch: Partial<T>): T {
  const next = { ...base };
  for (const key of Object.keys(patch) as (keyof T)[]) {
    const v = patch[key];
    if (v !== undefined) (next as T)[key] = v as T[keyof T];
  }
  return next;
}

// Append `record` unless a row with the same id already exists. The
// id-guard makes offline drain replay-safe (apply the same op twice →
// one row).
export function appendById<T extends { id: string }>(
  list: T[] | undefined,
  record: T,
): T[] {
  const arr = list ?? [];
  return arr.some((x) => x.id === record.id) ? arr : [...arr, record];
}

// Merge `patch`'s defined keys into the row with `id`. Returns the same
// array reference when no row matched.
export function patchById<T extends { id: string }>(
  list: T[] | undefined,
  id: string,
  patch: Partial<T>,
): T[] {
  const arr = list ?? [];
  let touched = false;
  const next = arr.map((row) => {
    if (row.id !== id) return row;
    touched = true;
    return mergeDefined(row, patch);
  });
  return touched ? next : arr;
}

// Remove the row with `id`. Returns the same array reference when
// nothing matched. NOTE: the ledger never hard-removes (tombstones
// only) — kept for parity with trip-state-core and for future purge
// tooling.
export function removeById<T extends { id: string }>(
  list: T[] | undefined,
  id: string,
): T[] {
  const arr = list ?? [];
  const next = arr.filter((x) => x.id !== id);
  return next.length === arr.length ? arr : next;
}

// ─── Ops ─────────────────────────────────────────────────────────────
//
// Discriminated union. `opId` identifies the op itself — the server
// dedupes against state._meta.recentOpIds so a queue replay is a no-op;
// embedded record ids are part of the intent. Update/delete ops carry
// the acting email so the reducer can stamp updatedBy/deletedBy without
// reaching outside the op (keeps applyOp pure AND idempotent — applying
// the same op twice yields a value-identical state because every stamp
// comes from op fields, never from a fresh clock read).

export type ExpensePatch = Partial<{
  date: string;
  dateEnd: string | null;
  vendor: string;
  note: string | null;
  amountCents: number;
  currency: string;
  baseAmountCents: number;
  paidBy: string;
  split: Split;
  category: string | null;
  payerSource: string | null;
  source: ExpenseSource | null;
}>;

export type SettlementPatch = Partial<{
  date: string;
  from: string;
  to: string;
  amountCents: number;
  currency: string;
  baseAmountCents: number;
  method: string | null;
  note: string | null;
}>;

export type LedgerOp =
  | { kind: 'addExpense'; opId: string; createdAt: string; record: Expense }
  | {
      kind: 'updateExpense';
      opId: string;
      createdAt: string;
      expenseId: string;
      patch: ExpensePatch;
      updatedBy: string;
    }
  | {
      kind: 'deleteExpense';
      opId: string;
      createdAt: string;
      expenseId: string;
      deletedBy: string;
    }
  | { kind: 'addSettlement'; opId: string; createdAt: string; record: Settlement }
  | {
      kind: 'updateSettlement';
      opId: string;
      createdAt: string;
      settlementId: string;
      patch: SettlementPatch;
      updatedBy: string;
    }
  | {
      kind: 'deleteSettlement';
      opId: string;
      createdAt: string;
      settlementId: string;
      deletedBy: string;
    };

// The record id an op touches — recorded next to the opId in
// _meta.recentOps so a deduped replay can re-serve the committed record.
export function opRecordId(op: LedgerOp): string {
  switch (op.kind) {
    case 'addExpense':
    case 'addSettlement':
      return op.record.id;
    case 'updateExpense':
    case 'deleteExpense':
      return op.expenseId;
    case 'updateSettlement':
    case 'deleteSettlement':
      return op.settlementId;
  }
}

// ─── Reducer ─────────────────────────────────────────────────────────
// Pure: take state + op, return next state. All branches are
// idempotent — applying the same op twice is harmless (add is id-
// guarded, update stamps come from the op, delete short-circuits on an
// existing tombstone), and update/delete on a missing id is a clean
// no-op (returns the same state reference) so a replay against a purged
// record can never throw.

export function applyOp(state: LedgerState, op: LedgerOp): LedgerState {
  switch (op.kind) {
    case 'addExpense': {
      const list = state.expenses ?? [];
      const next = appendById(list, op.record);
      if (next === list) return state;
      return { ...state, expenses: next };
    }
    case 'updateExpense': {
      const list = state.expenses ?? [];
      const next = patchById(list, op.expenseId, {
        ...op.patch,
        updatedAt: op.createdAt,
        updatedBy: op.updatedBy,
      } as Partial<Expense>);
      if (next === list) return state;
      return { ...state, expenses: next };
    }
    case 'deleteExpense': {
      const list = state.expenses ?? [];
      const row = list.find((x) => x.id === op.expenseId);
      if (!row || row.deleted === true) return state;
      const next = patchById(list, op.expenseId, {
        deleted: true,
        deletedAt: op.createdAt,
        deletedBy: op.deletedBy,
      } as Partial<Expense>);
      return { ...state, expenses: next };
    }
    case 'addSettlement': {
      const list = state.settlements ?? [];
      const next = appendById(list, op.record);
      if (next === list) return state;
      return { ...state, settlements: next };
    }
    case 'updateSettlement': {
      const list = state.settlements ?? [];
      const next = patchById(list, op.settlementId, {
        ...op.patch,
        updatedAt: op.createdAt,
        updatedBy: op.updatedBy,
      } as Partial<Settlement>);
      if (next === list) return state;
      return { ...state, settlements: next };
    }
    case 'deleteSettlement': {
      const list = state.settlements ?? [];
      const row = list.find((x) => x.id === op.settlementId);
      if (!row || row.deleted === true) return state;
      const next = patchById(list, op.settlementId, {
        deleted: true,
        deletedAt: op.createdAt,
        deletedBy: op.deletedBy,
      } as Partial<Settlement>);
      return { ...state, settlements: next };
    }
    default: {
      // Exhaustiveness check — adding a new op kind without handling
      // it here will surface as a compile error.
      const _: never = op;
      void _;
      return state;
    }
  }
}

export function applyOps(state: LedgerState, ops: LedgerOp[]): LedgerState {
  let next = state;
  for (const op of ops) next = applyOp(next, op);
  return next;
}

// ─── Split math ──────────────────────────────────────────────────────

// Who participates in an expense's split, in DETERMINISTIC (lexicographic
// email) order. equal → every current household member; shares/exact →
// the keyed emails; full → just owedBy.
function splitParticipants(expense: Expense, memberEmails: string[]): string[] {
  const split = expense.split ?? { type: 'equal' };
  let emails: string[];
  switch (split.type) {
    case 'equal':
      emails = [...memberEmails];
      break;
    case 'exact':
      emails = Object.keys(split.sharesCents);
      break;
    case 'shares':
      emails = Object.keys(split.shares);
      break;
    case 'full':
      emails = [split.owedBy];
      break;
    default:
      emails = [...memberEmails];
  }
  return emails.sort();
}

// THE rounding rule (plan §Locked decisions) — this function is the only
// place split rounding exists in the entire system:
//
//   Floor each participant's share, then give every leftover minor unit
//   to the PAYER's share. Rationale: the payer fronted the money, so
//   rounding error resolves in their favor without anyone Venmo-ing a
//   fraction. If the payer is NOT in the participant set (e.g. Jake pays
//   but the split is full/owedBy:liz, or an exact split that excludes
//   him), the leftover goes to the FIRST participant by lexicographic
//   email — deterministic across every surface, no clock or
//   insertion-order dependence.
//
// Hand the unallocated remainder to `paidBy` when present, else to the
// first participant. Shared by the base split and the exact conversion
// so the two can never diverge.
//
// WHY IT ACCEPTS A NEGATIVE DELTA. Flooring can only ever UNDER-allocate
// (delta ≥ 0) as long as each `Math.floor(a * b / c)` is computed
// exactly. That holds while the product `a * b` stays inside IEEE-754's
// exact-integer range: the true quotient is a rational with denominator
// c, so it sits either exactly on an integer or at least 1/c away from
// one, while the division's rounding error is at most half an ulp —
// and 1/c > ulp/2 is precisely the condition `a * b < 2^53`. Every
// realistic ledger amount is many orders of magnitude inside that (it
// would take a ~$90,000,000,000,000 expense to reach it).
//
// Rather than rest the Σnet≡0 invariant on that argument, the delta is
// applied whatever its sign: the shares then sum to the target BY
// CONSTRUCTION, for any input, and the float bound only decides WHO
// absorbs a unit — never whether the ledger balances.
function assignLeftover(
  out: Record<string, number>,
  participants: string[],
  paidBy: string,
  leftover: number,
): void {
  if (leftover === 0 || participants.length === 0) return;
  const receiver = paidBy in out ? paidBy : participants[0];
  out[receiver] += leftover;
}

// SHARES ARE IN BASE UNITS (USD cents), always — balances are expressed
// in the household base currency, so the thing that must divide exactly
// is baseAmountCents, not the native amount. For a USD expense the two
// are equal and this is bit-for-bit the pre-multi-currency behavior.
//
// Returns { email → integer USD cents }; values always sum EXACTLY to
// baseAmountOf(expense).
export function expenseShares(
  expense: Expense,
  memberEmails: string[],
): Record<string, number> {
  const split = expense.split ?? { type: 'equal' };
  const base = baseAmountOf(expense);

  if (split.type === 'exact') {
    return exactBaseShares(expense, split.sharesCents, base);
  }
  if (split.type === 'full') {
    return { [split.owedBy]: base };
  }

  const participants = splitParticipants(expense, memberEmails);
  if (participants.length === 0) return {};

  const out: Record<string, number> = {};
  let allocated = 0;
  if (split.type === 'shares') {
    let totalWeight = 0;
    for (const email of participants) totalWeight += split.shares[email];
    if (totalWeight <= 0) return {};
    for (const email of participants) {
      const share = Math.floor((base * split.shares[email]) / totalWeight);
      out[email] = share;
      allocated += share;
    }
  } else {
    // equal
    const each = Math.floor(base / participants.length);
    for (const email of participants) {
      out[email] = each;
      allocated += each;
    }
  }

  assignLeftover(out, participants, expense.paidBy, base - allocated);
  return out;
}

// Convert an exact split's NATIVE per-member shares into BASE units.
//
// `sharesCents` is denominated in the expense's own currency and sums
// to amountCents (validator-enforced). Balances need base units, so
// each member's native share is scaled by the record's own implied rate
// — baseAmountCents / amountCents — using the SAME deterministic rule
// as every other split: floor each converted share, then hand the
// leftover to the payer (lexicographically-first participant when the
// payer isn't one).
//
// WHY THE SUM IS EXACT, not approximately exact:
//   Σ floor(nᵢ·B/A) ≤ floor((Σnᵢ)·B/A) = floor(A·B/A) = B, and each
//   floor loses < 1, so the deficit is an integer in [0, k) for k
//   participants. Handing that whole deficit to one participant makes
//   Σ base shares === B by construction — for any ratio, any exponent,
//   any number of members. (Unit-tested by sweep; assignLeftover closes
//   the IEEE-754 corner too.)
//
// Pass-through short-circuit: when base === native (a base-currency
// expense) the scaling is the identity, so the stored shares are
// returned untouched — no rounding path runs at all and pure-USD
// behavior is provably unchanged.
function exactBaseShares(
  expense: Expense,
  sharesCents: Record<string, number>,
  base: number,
): Record<string, number> {
  const native = expense.amountCents;
  if (base === native) return { ...sharesCents };
  if (!Number.isFinite(native) || native <= 0) return { ...sharesCents };

  const participants = Object.keys(sharesCents).sort();
  const out: Record<string, number> = {};
  let allocated = 0;
  for (const email of participants) {
    const converted = Math.floor((sharesCents[email] * base) / native);
    out[email] = converted;
    allocated += converted;
  }
  assignLeftover(out, participants, expense.paidBy, base - allocated);
  return out;
}

// ─── Balances ────────────────────────────────────────────────────────

// EVERY FIELD HERE IS IN BASE-CURRENCY (USD) CENTS — never a native
// amount. A ¥48,000 dinner contributes its baseAmountCents (e.g.
// 31245), so the numbers stay addable across currencies.
export interface MemberBalance {
  email: string;
  paidCents: number; // expenses they fronted
  shareCents: number; // their share of all expenses
  settledNetCents: number; // settlements sent − settlements received
  netCents: number; // paid − share + settledNet; >0 ⇒ they are owed
}

export interface PairwiseBalance {
  from: string; // debtor
  to: string; // creditor
  amountCents: number; // always > 0
}

export interface Balances {
  members: MemberBalance[];
  pairwise: PairwiseBalance[];
}

// Compute per-member balances + pairwise who-owes-whom rows. Pure and
// deterministic; tombstoned records excluded. INVARIANT (unit-tested):
// Σ netCents === 0 — expense shares sum exactly to baseAmountCents by
// the expenseShares construction, and settlements are zero-sum
// transfers.
//
// MULTI-CURRENCY: only BASE amounts are summed here (baseAmountOf).
// Native amountCents never enters the arithmetic — mixing ¥ and $
// integers would produce a confidently wrong number.
//
// The member universe is memberEmails ∪ every email appearing on a
// record, so a since-removed member's history still balances instead of
// leaking cents out of the invariant.
//
// NOTE: equal splits divide over the CURRENT member roster — historical
// equal-split expenses re-divide if the roster changes. Accepted for v1
// (2-person household, roster changes ~never); exact/shares/full pin
// their participants explicitly.
export function computeBalances(
  expenses: Expense[],
  settlements: Settlement[],
  memberEmails: string[],
): Balances {
  const universe = new Set<string>(memberEmails);
  const paid: Record<string, number> = {};
  const share: Record<string, number> = {};
  const settledNet: Record<string, number> = {};

  for (const e of expenses) {
    if (e.deleted === true) continue;
    universe.add(e.paidBy);
    paid[e.paidBy] = (paid[e.paidBy] ?? 0) + baseAmountOf(e);
    const shares = expenseShares(e, memberEmails);
    for (const [email, cents] of Object.entries(shares)) {
      universe.add(email);
      share[email] = (share[email] ?? 0) + cents;
    }
  }

  for (const s of settlements) {
    if (s.deleted === true) continue;
    universe.add(s.from);
    universe.add(s.to);
    const moved = baseAmountOf(s);
    settledNet[s.from] = (settledNet[s.from] ?? 0) + moved;
    settledNet[s.to] = (settledNet[s.to] ?? 0) - moved;
  }

  const members: MemberBalance[] = [...universe].sort().map((email) => {
    const p = paid[email] ?? 0;
    const sh = share[email] ?? 0;
    const st = settledNet[email] ?? 0;
    return {
      email,
      paidCents: p,
      shareCents: sh,
      settledNetCents: st,
      netCents: p - sh + st,
    };
  });

  // Pairwise settlement plan: greedy debtor→creditor matching over
  // email-sorted lists. Deterministic; for the 2-person household this
  // is exactly one row (or none when settled up).
  const debtors = members
    .filter((m) => m.netCents < 0)
    .map((m) => ({ email: m.email, cents: -m.netCents }));
  const creditors = members
    .filter((m) => m.netCents > 0)
    .map((m) => ({ email: m.email, cents: m.netCents }));
  const pairwise: PairwiseBalance[] = [];
  let di = 0;
  let ci = 0;
  while (di < debtors.length && ci < creditors.length) {
    const owe = Math.min(debtors[di].cents, creditors[ci].cents);
    pairwise.push({ from: debtors[di].email, to: creditors[ci].email, amountCents: owe });
    debtors[di].cents -= owe;
    creditors[ci].cents -= owe;
    if (debtors[di].cents === 0) di += 1;
    if (creditors[ci].cents === 0) ci += 1;
  }

  return { members, pairwise };
}

// ─── Validators (pure, no I/O — trip-admin.js result idiom) ──────────
//
// Each returns { error, message } on rejection or { value } on success
// so callers decide the HTTP shape. Full-record validators run on the
// CONSTRUCTED record (add) or the MERGED preview (update), so cross-
// field rules like "exact sums to amountCents" hold after any patch.

type Invalid = { error: string; message: string };
type Valid<T> = { value: T };
export type Validation<T> = Invalid | Valid<T>;

export function isValidEmail(s: unknown): s is string {
  return typeof s === 'string' && s.length <= 254 && EMAIL_RE.test(s);
}

// Shape AND calendar validity — the regex alone accepted 2026-13-45 /
// Feb 30, which then evaded every since/until window and the mobile
// month grouping (review M1.1 finding). UTC round-trip: construct the
// date, then require every component to survive unchanged (JS Date
// silently rolls Feb 30 → Mar 1/2, which the compare catches).
export function isValidDate(s: unknown): s is string {
  if (typeof s !== 'string' || !ISO_DATE_RE.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
  );
}

export function validateAmountCents(v: unknown): Validation<number> {
  if (typeof v !== 'number' || !Number.isInteger(v) || v <= 0) {
    return {
      error: 'invalid_amount',
      message:
        'amountCents must be an integer number of MINOR UNITS of the record currency, > 0 ' +
        '($152.34 USD → 15234; ¥48,000 JPY → 48000, because JPY has no minor unit)',
    };
  }
  return { value: v };
}

export function validateBaseAmountCents(v: unknown): Validation<number> {
  if (typeof v !== 'number' || !Number.isInteger(v) || v <= 0) {
    return {
      error: 'invalid_base_amount',
      message:
        'baseAmountCents must be an integer number of USD cents > 0 — the amount in the ' +
        'household base currency (balances are computed from it). It is required on every ' +
        'record; for a record already in the base currency it must equal amountCents.',
    };
  }
  return { value: v };
}

// Validate a split against the amount it divides. memberEmails guards
// equal splits from dividing over an empty roster.
export function validateSplit(
  split: unknown,
  amountCents: number,
): Validation<Split> {
  if (!split || typeof split !== 'object') {
    return { error: 'invalid_split', message: 'split must be an object' };
  }
  const s = split as Record<string, unknown>;
  switch (s.type) {
    case 'equal':
      return { value: { type: 'equal' } };
    case 'exact': {
      const shares = s.sharesCents;
      if (!shares || typeof shares !== 'object' || Array.isArray(shares)) {
        return { error: 'invalid_split', message: 'exact split requires sharesCents object' };
      }
      const entries = Object.entries(shares as Record<string, unknown>);
      if (entries.length === 0) {
        return { error: 'invalid_split', message: 'sharesCents must have at least one entry' };
      }
      let sum = 0;
      const out: Record<string, number> = {};
      for (const [email, cents] of entries) {
        if (!isValidEmail(email)) {
          return { error: 'invalid_split', message: `sharesCents key "${email}" is not an email` };
        }
        if (typeof cents !== 'number' || !Number.isInteger(cents) || cents < 0) {
          return {
            error: 'invalid_split',
            message: `sharesCents["${email}"] must be a non-negative integer`,
          };
        }
        // Emails normalize to lowercase (review M2.1: mixed-case keys
        // stored verbatim forked balance identity — computeBalances
        // keys by exact string). Duplicate-after-lowercase would
        // silently drop a share, so it's an explicit error.
        const key = email.toLowerCase();
        if (key in out) {
          return {
            error: 'invalid_split',
            message: `sharesCents key "${email}" duplicates "${key}" after lowercasing`,
          };
        }
        sum += cents;
        out[key] = cents;
      }
      if (sum !== amountCents) {
        return {
          error: 'split_sum_mismatch',
          message: `sharesCents sum ${sum} must equal amountCents ${amountCents}`,
        };
      }
      return { value: { type: 'exact', sharesCents: out } };
    }
    case 'shares': {
      const weights = s.shares;
      if (!weights || typeof weights !== 'object' || Array.isArray(weights)) {
        return { error: 'invalid_split', message: 'shares split requires shares object' };
      }
      const entries = Object.entries(weights as Record<string, unknown>);
      if (entries.length === 0) {
        return { error: 'invalid_split', message: 'shares must have at least one entry' };
      }
      const out: Record<string, number> = {};
      for (const [email, w] of entries) {
        if (!isValidEmail(email)) {
          return { error: 'invalid_split', message: `shares key "${email}" is not an email` };
        }
        if (typeof w !== 'number' || !Number.isInteger(w) || w <= 0) {
          return {
            error: 'invalid_split',
            message: `shares["${email}"] must be a positive integer`,
          };
        }
        const key = email.toLowerCase();
        if (key in out) {
          return {
            error: 'invalid_split',
            message: `shares key "${email}" duplicates "${key}" after lowercasing`,
          };
        }
        out[key] = w;
      }
      return { value: { type: 'shares', shares: out } };
    }
    case 'full': {
      if (!isValidEmail(s.owedBy)) {
        return { error: 'invalid_split', message: 'full split requires owedBy email' };
      }
      return { value: { type: 'full', owedBy: s.owedBy.toLowerCase() } };
    }
    default:
      return {
        error: 'invalid_split',
        message: 'split.type must be equal | exact | shares | full',
      };
  }
}

const SOURCE_APP_RE = /^[a-z0-9-]{1,40}$/;

// Validate the cross-app import link. Absent/null is VALID (a normal
// manual expense) and normalizes to null. When present it must be the
// complete triple — a partial key can't identify a booking, and the
// household-uniqueness rule in ledger-ops is only meaningful on a
// complete one. ':' is banned inside tripId/bookingId because it is the
// sourceKey separator ("tk:a:b:c" would be ambiguous).
//
// Every rejection message starts with the same sentence so a caller
// (or a model driving the MCP tool) always learns the required shape.
export function validateSource(v: unknown): Validation<ExpenseSource | null> {
  if (v === undefined || v === null) return { value: null };
  const bad = (detail: string): Invalid => ({
    error: 'invalid_source',
    message: `source must be {app, tripId, bookingId} — ${detail}`,
  });
  if (typeof v !== 'object' || Array.isArray(v)) return bad('source is not an object');
  const s = v as Record<string, unknown>;
  if (typeof s.app !== 'string' || !SOURCE_APP_RE.test(s.app)) {
    return bad('app must be 1-40 chars of [a-z0-9-]');
  }
  for (const field of ['tripId', 'bookingId'] as const) {
    const raw = s[field];
    if (typeof raw !== 'string' || raw.length === 0 || raw.length > 64) {
      return bad(`${field} must be a string of 1-64 chars`);
    }
    if (raw.includes(':')) {
      return bad(`${field} must not contain ":" (the sourceKey separator)`);
    }
  }
  // Unknown keys are dropped, not stored — the key must stay exactly
  // these three fields on both sides of the integration.
  return {
    value: { app: s.app, tripId: s.tripId as string, bookingId: s.bookingId as string },
  };
}

// Record currency must be a SUPPORTED ISO 4217 code, normalized to
// uppercase. It no longer has to equal the household currency — a
// record may be written in any supported currency, because balances
// read baseAmountCents rather than summing raw native amounts (the
// M1.1 "a stray JPY record corrupts every balance" finding is now
// answered by the base amount, not by a single-currency rule).
//
// Unsupported codes are rejected rather than passed through: without a
// known minor-unit exponent the amount cannot be rendered or reasoned
// about, and a typo'd "USDD" would become a silent second currency.
export function validateCurrencyCode(currency: unknown): Validation<string> {
  if (typeof currency !== 'string' || currency.trim().length === 0) {
    return { error: 'invalid_currency', message: 'currency must be a non-empty string' };
  }
  const code = normalizeCurrencyCode(currency);
  if (code === null) {
    return {
      error: 'unsupported_currency',
      message:
        `currency ${currency.trim().toUpperCase()} is not supported — use one of: ` +
        SUPPORTED_CURRENCIES.join(', '),
    };
  }
  return { value: code };
}

// The currency + base-amount rules that both record validators share.
// Returns the normalized {currency, baseAmountCents} pair or an error.
//
// THE BASE-EQUALS-NATIVE RULE: when a record is denominated in the
// household base currency there is no conversion to be had, so
// baseAmountCents MUST equal amountCents. Enforcing it (rather than
// trusting the caller) is what stops a plain USD expense from ever
// drifting away from its own base amount — a drifted USD record would
// show one number on the row and settle a different one in the balance,
// which is the single most confusing bug this feature could ship.
function validateMoneyFields(
  record: { amountCents: number; currency: string; baseAmountCents?: number },
  baseCurrency: string,
): Validation<{ currency: string; baseAmountCents: number }> {
  const amount = validateAmountCents(record.amountCents);
  if ('error' in amount) return amount;
  const currency = validateCurrencyCode(record.currency);
  if ('error' in currency) return currency;
  const base = validateBaseAmountCents(record.baseAmountCents);
  if ('error' in base) return base;
  const baseCode = normalizeCurrencyCode(baseCurrency) ?? BASE_CURRENCY;
  if (currency.value === baseCode && base.value !== amount.value) {
    return {
      error: 'base_amount_mismatch',
      message:
        `baseAmountCents ${base.value} must equal amountCents ${amount.value} for a ` +
        `${baseCode} record (${baseCode} is the household base currency — there is nothing to convert)`,
    };
  }
  return { value: { currency: currency.value, baseAmountCents: base.value } };
}

// Validate a complete Expense record (post-construction / post-merge).
// `baseCurrency` is the household's meta.currency — the currency
// baseAmountCents is denominated in (USD). It is NOT a whitelist for
// `currency`: any supported code may be written, but a record written
// IN the base currency must carry base === native.
//
// NORMALIZATION: the returned value carries the uppercased currency
// code alongside the lowercased emails. Callers MUST store the
// validator's `value`.
//
// EMAIL NORMALIZATION (review M2.1): the returned value carries every
// referenced email lowercased — paidBy here, split keys/owedBy inside
// validateSplit. computeBalances keys by exact string, so a stored
// mixed-case email would fork one person into two balance identities
// ("Jakewaage@…" owing "jakewaage@…"). Callers MUST store the
// validator's `value`, never the raw input record.
export function validateExpenseRecord(
  record: Expense,
  baseCurrency: string = BASE_CURRENCY,
): Validation<Expense> {
  if (!isValidDate(record.date)) {
    return { error: 'invalid_date', message: 'date must be a real YYYY-MM-DD calendar date' };
  }
  if (record.dateEnd !== undefined && record.dateEnd !== null && !isValidDate(record.dateEnd)) {
    return { error: 'invalid_date', message: 'dateEnd must be a real YYYY-MM-DD calendar date' };
  }
  if (typeof record.vendor !== 'string' || record.vendor.trim().length === 0 || record.vendor.length > 120) {
    return { error: 'invalid_vendor', message: 'vendor must be 1-120 chars' };
  }
  const money = validateMoneyFields(record, baseCurrency);
  if ('error' in money) return money;
  if (!isValidEmail(record.paidBy)) {
    return { error: 'invalid_paidBy', message: 'paidBy must be a valid email' };
  }
  // Exact shares are NATIVE units — they must sum to amountCents, not
  // to baseAmountCents (see the Split union's units note). The
  // native→base conversion happens later, in expenseShares.
  const split = validateSplit(record.split ?? { type: 'equal' }, record.amountCents);
  if ('error' in split) return split;
  const source = validateSource(record.source);
  if ('error' in source) return source;
  const value: Expense = {
    ...record,
    currency: money.value.currency,
    baseAmountCents: money.value.baseAmountCents,
    paidBy: record.paidBy.toLowerCase(),
    split: split.value,
  };
  // Only overwrite when a source was actually supplied — an absent
  // `source` must stay absent (writing an explicit null onto every
  // manual expense would churn the blob for nothing).
  if (source.value !== null) value.source = source.value;
  return { value };
}

// Validate a complete Settlement record (post-construction / post-merge).
// Like validateExpenseRecord, the returned value carries from/to
// lowercased — and from ≠ to is checked case-insensitively so
// "A@x.com" → "a@x.com" can't sneak past as a self-settlement.
export function validateSettlementRecord(
  record: Settlement,
  baseCurrency: string = BASE_CURRENCY,
): Validation<Settlement> {
  if (!isValidDate(record.date)) {
    return { error: 'invalid_date', message: 'date must be a real YYYY-MM-DD calendar date' };
  }
  if (!isValidEmail(record.from)) {
    return { error: 'invalid_from', message: 'from must be a valid email' };
  }
  if (!isValidEmail(record.to)) {
    return { error: 'invalid_to', message: 'to must be a valid email' };
  }
  if (record.from.toLowerCase() === record.to.toLowerCase()) {
    return { error: 'invalid_settlement', message: 'from and to must differ' };
  }
  const money = validateMoneyFields(record, baseCurrency);
  if ('error' in money) return money;
  return {
    value: {
      ...record,
      currency: money.value.currency,
      baseAmountCents: money.value.baseAmountCents,
      from: record.from.toLowerCase(),
      to: record.to.toLowerCase(),
    },
  };
}
